import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { ContactDialog } from './ContactDialog';
import { ContactHoverCard } from './ContactHoverCard';
import { ContactRound, Plus, Search, UserRound, BadgeCheck, X, Loader2, Mail, Briefcase, Home, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  'Broker': 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  'Owner': 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  'OLTA Investor': 'border-violet-500/40 bg-violet-500/10 text-violet-300',
  'Equity Gap Investor': 'border-amber-500/40 bg-amber-500/10 text-amber-300',
};
const typeClass = (t) => TYPE_COLORS[t] || 'border-border bg-secondary/40 text-muted-foreground';

// CRM contacts linked to this deal (brokers, advisors, owners, investors …)
export function DealLinkedContactsCard({ projectId, companyName = '' }) {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [types, setTypes] = useState([]);

  const load = useCallback(() => {
    api.getProjectContacts(projectId)
      .then(r => setContacts(r.data.contacts || []))
      .catch(() => {});
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(() => {
      setSearching(true);
      api.getContacts(search, '')
        .then(r => setResults((r.data.contacts || []).filter(c => !contacts.some(l => l.id === c.id)).slice(0, 8)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [pickerOpen, search, contacts]);

  const link = async (c) => {
    try {
      await api.linkContactToProject(c.id, projectId);
      toast.success(`${[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company} linked to this deal`);
      setPickerOpen(false); setSearch('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to link'); }
  };

  const unlink = async (c) => {
    try {
      await api.unlinkContactFromProject(c.id, projectId);
      toast.success('Contact unlinked (still in the Contacts database)');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openNewContact = async () => {
    try { const r = await api.getContactTypes(); setTypes(r.data || []); } catch { setTypes([]); }
    setDialogOpen(true);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="deal-linked-contacts-card">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ContactRound className="w-4 h-4 text-violet-400" />
          <h3 className="font-heading text-base text-foreground">Linked Contacts</h3>
          <span className="text-[10px] text-muted-foreground">{contacts.length} · from the Contacts database</span>
        </div>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" onClick={() => { setPickerOpen(!pickerOpen); setSearch(''); }}
            data-testid="link-contact-btn"
            className="border-violet-600/40 text-violet-400 hover:bg-violet-600/10 rounded-sm text-[11px] h-7 px-2.5">
            <Search className="w-3 h-3 mr-1" /> Link contact
          </Button>
          <Button variant="outline" size="sm" onClick={openNewContact} data-testid="linked-new-contact-btn"
            className="border-emerald-600/40 text-emerald-400 hover:bg-emerald-600/10 rounded-sm text-[11px] h-7 px-2.5">
            <Plus className="w-3 h-3 mr-1" /> New contact
          </Button>
        </div>
      </div>

      {pickerOpen && (
        <div className="mb-3 border border-border rounded-sm p-2 bg-secondary/20" data-testid="link-contact-picker">
          <div className="relative mb-1.5">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} autoFocus
              placeholder="Search contacts by name, company, email…" data-testid="link-contact-search"
              className="pl-7 bg-secondary border-border text-foreground rounded-sm h-7 text-xs" />
          </div>
          {searching ? (
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground px-1 py-1"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</p>
          ) : results.length === 0 ? (
            <p className="text-[11px] text-muted-foreground px-1 py-1">No unlinked contacts found — create one via &ldquo;New contact&rdquo;.</p>
          ) : (
            <div className="space-y-0.5 max-h-48 overflow-y-auto">
              {results.map((c, i) => (
                <button key={c.id} onClick={() => link(c)} data-testid={`link-contact-result-${i}`}
                  className="w-full flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-secondary/60 text-left transition-colors">
                  <span className="w-6 h-6 rounded-full overflow-hidden bg-secondary/60 border border-border shrink-0 flex items-center justify-center">
                    {c.has_photo
                      ? <img src={`${API_BASE}/api/contacts/${c.id}/photo/file`} alt="" className="w-full h-full object-cover" loading="lazy" />
                      : <UserRound className="w-3 h-3 text-muted-foreground/40" />}
                  </span>
                  <span className="text-xs text-foreground truncate">{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company}</span>
                  {c.company && <span className="text-[10px] text-muted-foreground truncate">· {c.company}</span>}
                  {c.type && <span className={`ml-auto text-[9px] font-mono border rounded px-1 py-0.5 shrink-0 ${typeClass(c.type)}`}>{c.type}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {contacts.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No CRM contacts linked yet — link the broker, advisors or owners so the whole team knows who is involved in this deal.
        </p>
      ) : (
        <div className="space-y-1.5">
          {contacts.map((c, i) => (
            <div key={c.id} className="flex items-center gap-2.5 px-2.5 py-2 bg-secondary/30 border border-border rounded-sm group"
              data-testid={`linked-contact-row-${i}`}>
              <span className="w-8 h-8 rounded-full overflow-hidden bg-secondary/60 border border-border shrink-0 flex items-center justify-center">
                {c.has_photo
                  ? <img src={`${API_BASE}/api/contacts/${c.id}/photo/file`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  : <UserRound className="w-4 h-4 text-muted-foreground/40" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <ContactHoverCard name={[c.first_name, c.last_name].filter(Boolean).join(' ')} email={c.email}>
                    <span className="text-xs font-medium text-foreground truncate cursor-pointer underline decoration-dotted decoration-foreground/20 underline-offset-2 hover:decoration-foreground/60">
                      {[c.title, c.first_name, c.last_name].filter(Boolean).join(' ') || c.company}
                    </span>
                  </ContactHoverCard>
                  {c.type && <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 shrink-0 ${typeClass(c.type)}`}>{c.type}</span>}
                  {c.has_photo && c.photo_verified && <BadgeCheck className="w-3 h-3 text-emerald-400 shrink-0" title="Photo verified" />}
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {[c.company, c.email].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              {c.email && (
                <a href={`mailto:${c.email}`} className="text-muted-foreground hover:text-blue-400 shrink-0" title={c.email}>
                  <Mail className="w-3.5 h-3.5" />
                </a>
              )}
              {(c.phone_work || c.phone_private) && (
                <span className="hidden sm:flex flex-col text-[10px] text-muted-foreground shrink-0">
                  {c.phone_work && <span className="flex items-center gap-1"><Briefcase className="w-2.5 h-2.5" />{c.phone_work}</span>}
                  {c.phone_private && <span className="flex items-center gap-1"><Home className="w-2.5 h-2.5" />{c.phone_private}</span>}
                </span>
              )}
              <button onClick={() => navigate(`/contacts?edit=${c.id}`)} data-testid={`open-contact-${i}`}
                title="Open on Contacts page"
                className="text-muted-foreground hover:text-emerald-400 transition-colors shrink-0">
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => unlink(c)} data-testid={`unlink-contact-${i}`} title="Unlink from this deal"
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400 transition-all shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <ContactDialog open={dialogOpen} onOpenChange={setDialogOpen} contact={null}
        types={types} isAdmin={isAdmin} projectId={projectId} defaultCompany="" onSaved={load} />
    </div>
  );
}
